import React, { useContext, useEffect, useState, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import PageWrapper from "../../../components/PageWrapper";
import useLanguage from "../../../utils/useLanguage";
import { useTranslation } from "react-i18next";
import CustomInput from "../../../components/CustomInput";
import CustomButton from "../../../components/CustomButton";
import searchIcon from "../../../assets/search_icon.png";
import printIcon from "../../../assets/printer_icon.png";
import previousIcon from "../../../assets/previous_icon.png";
import nextIcon from "../../../assets/next_icon.png";
import saveIcon from "../../../assets/save_icon.png";
import axios from "axios";
import SectionDivider from "../../../components/SectionDivider";
import useRequest from "../../../utils/useRequest";
import { UserAuth } from "../../../utils/AuthContext";
import BackToDashboard from "../../../components/BackToDashboard";
import Loader from "../../../components/Loader";
import AreYouSureModal from "../../../components/AreYouSureModal";
import PageHeadline from "../../../components/PageHeadline";
import ProductOrderList from "./ProductOrderList";
import SelectedProductOrderList from "./SelectedProductOrderList";
import { getPaymentCode } from "../../../services/PaymentCodeService";
import useDebounce from "../../../utils/useDebounce";

const AddBookOrder = () => {
  const { currentDirection } = useLanguage();
  const { t } = useTranslation("general");
  const { userData } = useContext(UserAuth);
  const printRef = useRef(null);

  //  STATES
  const [selectedBooks, setSelectedBooks] = useState({});
  const [allSelectedBooks, setAllSelectedBooks] = useState([]);
  const [isPrinting, setIsPrinting] = useState(false);
  const [orderDetails, setOrderDetails] = useState({
    orderNumber: "",
    date: new Date().toLocaleDateString(),
    companyName: "",
    createdBy: "",
  });
  const [showAreYouSure, setAreYouSure] = useState(false);
  const [receiptNumber, setReceiptNumber] = useState("");

  const [loadingBooks, setLoadingBooks] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 500);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  });

  const [booksData, setBooksData] = useState([]);
  const [orderData, setOrderData] = useState({ orderNumber: null, date: null });
  const [selectedCurrency, setSelectedCurrency] = useState({
    title: "usd",
    value: "USD",
  });

  // Book headers configuration
  const bookHeaders = [
    { key: "checkbox", label: "choose", type: "checkbox" },
    { key: "title", label: "bookName", type: "text" },
    { key: "bookType", label: "bookType", type: "text" },
    { key: "academicLevel", label: "academicLevel", type: "text" },
    // { key: "isbn", label: "isbn", type: "text" },
    { key: "quantity", label: "quantity", type: "input", unit: "book" },
    { key: "price", label: "price", type: "input", unit: "currency" },
  ];

  // Fake data for testing
  const fakeBookData = [
    {
      id: "CATEGORY_1",
      category: "Programming",
      books: [
        {
          id: 1,
          title: "JavaScript: The Good Parts",
          author: "Douglas Crockford",
          isbn: "978-0596517748",
          price: 29.99,
          quantity: 0,
        },
        {
          id: 2,
          title: "Clean Code",
          author: "Robert Martin",
          // isbn: "978-0132350884",
          price: 35.99,
          quantity: 0,
        },
        {
          id: 3,
          title: "React Patterns",
          author: "Michael Chan",
          // isbn: "978-1492051718",
          price: 42.99,
          quantity: 0,
        },
      ],
    },
    // {
    //   id: "CATEGORY_2",
    //   category: "Design",
    //   books: [
    //     {
    //       id: 4,
    //       title: "Don't Make Me Think",
    //       author: "Steve Krug",
    //       isbn: "978-0321965516",
    //       price: 25.99,
    //       quantity: 0,
    //     },
    //     {
    //       id: 5,
    //       title: "The Design of Everyday Things",
    //       author: "Don Norman",
    //       isbn: "978-0465050659",
    //       price: 28.99,
    //       quantity: 0,
    //     },
    //   ],
    // },
    // {
    //   id: "CATEGORY_3",
    //   category: "Business",
    //   books: [
    //     {
    //       id: 6,
    //       title: "The Lean Startup",
    //       author: "Eric Ries",
    //       isbn: "978-0307887894",
    //       price: 24.99,
    //       quantity: 0,
    //     },
    //     {
    //       id: 7,
    //       title: "Zero to One",
    //       author: "Peter Thiel",
    //       isbn: "978-0804139298",
    //       price: 27.99,
    //       quantity: 0,
    //     },
    //   ],
    // },
  ];

  /* ENDPOINTS */

  const {
    loading: confirmOrderLoading,
    success,
    error,
    setError,
    data,
    refetch: handleSaveOrder,
  } = useRequest(
    `/createBookOrder`,
    "POST",
    {
      books: allSelectedBooks?.map((book) => ({
        ...book,
        currency: selectedCurrency.value,
      })),
      organizationId: parseInt(userData?.organizationId),
      orderNumber: orderData?.orderNumber,
      createdAt: new Date(orderData?.date).toISOString(),
      currency: selectedCurrency.value,
    },
    {},
    false
  );

  // Function to get order number using your specified function
  const getOrderNumber = async () => {
    try {
      const response = await getPaymentCode();
      if (response.status == 200) {
        setReceiptNumber(response?.data?.code);
        setOrderData((prev) => ({
          ...prev,
          orderNumber: response?.data?.code,
        }));
        setOrderDetails((prev) => ({
          ...prev,
          orderNumber: response?.data?.code,
          companyName: userData.organizationName,
          createdBy: userData.username,
        }));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getAllBooks = async () => {
    const organizationId = localStorage.getItem("organizationId");
    if (!organizationId) return;

    try {
      setLoadingBooks(true);

      // For testing, use fake data instead of API call
      setTimeout(() => {
        setBooksData(fakeBookData);
        setPagination({
          currentPage: 1,
          totalPages: 1,
          totalRecords: fakeBookData.length,
        });
        setLoadingBooks(false);
      }, 0); // Simulate loading delay

      /* Original API call - uncomment when ready to use real API
      const response = await axios.get(
        `${process.env.REACT_APP_URL}/getAllBooks`,
        {
          params: {
            organizationId,
            search: searchQuery || undefined,
            page: pagination.currentPage,
            limit: 5,
          },
        }
      );

      if (response.status === 200 && response.data?.data) {
        const formatted = response.data.data.map((item) => ({
          id: item.id,
          title: item.title,
          author: item.author,
          isbn: item.isbn,
          price: item.price,
          // Add other book properties as needed
        }));
        setBooksData(formatted);

        setPagination({
          currentPage: response.data?.pagination?.page,
          totalPages: response.data?.pagination?.totalPages,
          totalRecords: response.data?.pagination?.total,
        });
      }
      */
    } catch (err) {
      console.error("Failed to fetch books:", err);
      setLoadingBooks(false);
    }
  };

  /* HANDLER FUNCTIONS */
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    pageStyle: `
      @page { 
        size: A4; 
        margin: 5mm;
        width: 21cm;
        height: 29.7cm;
      }
      @media print {
        body { 
          -webkit-print-color-adjust: exact;
          padding: 10mm;
        }
        .printable-order-view { display: flex !important; }
      }
    `,
  });

  const handleConfirmOrder = () => {
    setAreYouSure(true);
  };

  const handlePrintAndSave = async () => {
    setAreYouSure(false);
    try {
      await handleSaveOrder();
    } catch (error) {
      console.error("Error saving order:", error);
    }
  };

  // Book selection handler
  const handleBookSelection = (categoryId, bookId, isSelected) => {
    setSelectedBooks((prev) => {
      if (bookId === null) {
        // Remove entire category
        const { [categoryId]: removed, ...rest } = prev;
        return rest;
      }

      return {
        ...prev,
        [categoryId]: {
          ...prev[categoryId],
          [bookId]: isSelected,
        },
      };
    });

    if (bookId === null) {
      // Remove entire category
      setAllSelectedBooks((prev) =>
        prev.filter((item) => item.category !== categoryId)
      );
    } else {
      if (isSelected) {
        // Add book to selected list
        const category = booksData.find((cat) => cat.category === categoryId);
        if (category) {
          const book = category.books.find((b) => b.id === bookId);
          if (book) {
            setAllSelectedBooks((prev) => {
              const categoryIndex = prev.findIndex(
                (cat) => cat.category === categoryId
              );

              if (categoryIndex >= 0) {
                // Category exists, add book to it
                const bookExists = prev[categoryIndex].books.some(
                  (b) => b.id === bookId
                );
                if (!bookExists) {
                  const updatedCategories = [...prev];
                  updatedCategories[categoryIndex] = {
                    ...updatedCategories[categoryIndex],
                    books: [
                      ...updatedCategories[categoryIndex].books,
                      { ...book, quantity: 1 },
                    ],
                  };
                  return updatedCategories;
                }
                return prev;
              } else {
                // New category
                return [
                  ...prev,
                  {
                    id: category.id,
                    category: categoryId,
                    books: [{ ...book, quantity: 1 }],
                  },
                ];
              }
            });
          }
        }
      } else {
        // Remove book from selected list
        setAllSelectedBooks((prev) => {
          const updatedCategories = prev.map((cat) => {
            if (cat.category === categoryId) {
              return {
                ...cat,
                books: cat.books.filter((book) => book.id !== bookId),
              };
            }
            return cat;
          });

          // Remove categories with no books
          return updatedCategories.filter((cat) => cat.books.length > 0);
        });
      }
    }
  };

  // Update input values for the selected books
  const handleInputChange = (categoryId, bookId, field, value) => {
    setAllSelectedBooks((prev) => {
      return prev.map((category) => {
        if (category.category === categoryId) {
          return {
            ...category,
            books: category.books.map((book) => {
              if (book.id === bookId) {
                return {
                  ...book,
                  [field]: value || 0,
                };
              }
              return book;
            }),
          };
        }
        return category;
      });
    });
  };

  /* USEEFECTS */
  useEffect(() => {
    getAllBooks();
  }, [debouncedSearch, pagination.currentPage]);

  useEffect(() => {
    getOrderNumber();
  }, []);

  useEffect(() => {
    if (success) {
      handlePrint();
      setOrderData({ orderNumber: null, date: null });
      setAllSelectedBooks([]);
      getAllBooks();
      getOrderNumber();
      setSearchQuery("");
    }
  }, [success]);

  // Sync selected books to display from our master list
  useEffect(() => {
    // This effect is no longer needed since selectedBooks is managed differently
  }, [allSelectedBooks]);

  return (
    <PageWrapper>
      {/* Back button */}
      <div>
        <BackToDashboard
          title={t("back")}
          variant="blank"
          icon={currentDirection === "ltr" ? previousIcon : nextIcon}
          iconSize={14}
          to={
            userData?.userType === "user"
              ? "/invoiceManagement"
              : "/bookstore-dashboard"
          }
        />
      </div>
      <div className="App o-column o-column--gap-size_400 u-padding--bottom_500">
        <PageHeadline title="addBookOrder" classList="u-margin--top_700" />
        <div className="o-row o-row--gap-size_400 o-row--horizontal-align_space-between o-row--vertical-align_end">
          <div className="o-row o-row--gap-size_400">
            <CustomInput
              placeholder={t("search")}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
              }}
              resetSearch={() => {
                setSearchQuery("");
              }}
              thick
            />

            <CustomButton
              title={t("search")}
              iconSize={15}
              classList="c-search-button"
              icon={searchIcon}
            />
          </div>

          <div className="o-row o-row--gap-size_300">
            <CustomInput
              title={t("orderNumber") + ": "}
              value={orderData?.orderNumber || receiptNumber}
              flex={false}
              readonly
              loading={loadingBooks}
            />
            <CustomInput
              title={t("Date") + ": "}
              value={new Date().toLocaleDateString()}
              flex={false}
              readonly
              loading={loadingBooks}
            />
          </div>
        </div>
        <SectionDivider title={t("theBooks")} />
        {loadingBooks && <Loader />}
        <ProductOrderList
          data={booksData}
          selectedItems={selectedBooks}
          onItemSelect={handleBookSelection}
          currency={selectedCurrency}
          onInputChange={handleInputChange}
          allSelectedItems={allSelectedBooks}
          headers={bookHeaders}
          groupByField="category"
          itemsField="theBooks"
          itemKeyField="id"
          groupLabelPrefix="academicLevel"
        />
        {pagination && (
          <section className="pagination">
            <div className="pagination-buttons">
              <CustomButton
                variant="blank"
                title={t("previous")}
                onClick={() =>
                  setPagination((prev) => ({
                    ...prev,
                    currentPage: prev.currentPage - 1,
                  }))
                }
                disabled={pagination.currentPage === 1}
                icon={currentDirection === "ltr" ? previousIcon : nextIcon}
                iconSize={15}
              />
              <span className="pagination-location">
                {t("page")}: {pagination.currentPage} - {pagination.totalPages}
              </span>
              <CustomButton
                title={t("next")}
                variant="blank"
                onClick={() =>
                  setPagination((prev) => ({
                    ...prev,
                    currentPage: prev.currentPage + 1,
                  }))
                }
                disabled={pagination.currentPage === pagination.totalPages}
                icon={currentDirection === "ltr" ? nextIcon : previousIcon}
                iconSize={15}
                reverseIcon
              />
            </div>
            <div className="pagination-buttons">
              <span className="pagination-location">
                {t("totalRecords")}: {pagination.totalRecords}
              </span>
            </div>
          </section>
        )}
        {allSelectedBooks.length > 0 && (
          <SectionDivider title={t("theAddedBooks")} />
        )}
        <SelectedProductOrderList
          data={allSelectedBooks}
          setSelectedItems={setAllSelectedBooks}
          currency={selectedCurrency}
          selectedItems={selectedBooks}
          onItemSelect={handleBookSelection}
          onInputChange={handleInputChange}
          headers={bookHeaders}
          groupByField="category"
          itemsField="theBooks"
          itemKeyField="id"
          groupLabelPrefix="academicLevel"
          showDeleteButton={true}
          deleteButtonLabel="delete"
        />
        {confirmOrderLoading && <Loader />}
        <div className="o-row o-row--horizontal-align_end o-row--gap-size_400">
          {allSelectedBooks.length > 0 && (
            <>
              <CustomButton
                title={t("confirm")}
                onClick={handleConfirmOrder}
                icon={saveIcon}
                iconSize={16}
                disabled={confirmOrderLoading || isPrinting}
              />
            </>
          )}
        </div>
      </div>

      {showAreYouSure && (
        <AreYouSureModal
          onClose={() => setAreYouSure(false)}
          onConfirm={handlePrintAndSave}
          customMessage={t("confirmOrderMessage")}
          closeTitle="cancel"
          confirmTitle="confirm"
          confirm={true}
          confirmIconType="whiteCheck"

        />
      )}

      {/* Printable Book Order View - You'll need to create this component */}
      {/* <PrintableBookOrderView
        ref={printRef}
        data={allSelectedBooks}
        currency={selectedCurrency}
        orderDetails={orderDetails}
      /> */}
    </PageWrapper>
  );
};

export default AddBookOrder;
